<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-orange" sprite="list-numbers" />
          Short Numbers
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-orange" sprite="list-numbers" />
            Short Numbers
          </h1>
        </el-col>
        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable> </el-input>
          <el-button class="ml-10 hidden-xs-only" @click="doExport">
            Export
          </el-button>
        </div>
        <div class="box right">
          <el-button @click="createVisible = true">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <create-form
      :createVisible="createVisible"
      :numInUse="numInUse"
      @checkForAvailableNum="checkNumber"
      @closeModal="closeModal"
    />

    <edit-form
      :editVisible="editVisible"
      :numInUse="numInUse"
      :clearFormFields="clearFormFields"
      :editProcessing="editProcessing"
      :selectedShortNumber="selectedShortNumber"
      @checkForAvailableNum="checkNumber"
      @cancelOperation="cancelOperation"
      @editOperation="editOperation"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from "lodash";

import feathersClient from "../../../feathers/feathers-client";
import createForm from "./components/createForm";
import editForm from "./components/editForm";
import { globalEventBus } from "@/globalEventBus";
import { ExportToCsv } from "export-to-csv";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "ShortNumbersTable",

  components: {
    createForm,
    editForm
  },

  data() {
    return {
      eventChannel: null,
      tableData: [],
      allNumbers: [],
      selectedShortNumber: null,
      search: "",
      visibleActionsId: "",
      tableLoading: false,
      createVisible: false,
      editProcessing: false,
      editVisible: false,
      numInUse: null,
      number: "",
      clearFormFields: false,
      total: 0,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-red" sprite="list-numbers" />
                </div>
              ];
            }
          }
        },
        {
          field: "number",
          title: "Number",
          sortable: true,
          minWidth: 150,
          slots: {
            default: ({ row }) => {
              return [
                <span>
                  {this.numberFormatter(row.number)}
                </span>
              ];
            }
          }
        },
        {
          field: "destnumber",
          title: "Destination Number",
          sortable: true,
          minWidth: 150,
          slots: {
            default: ({ row }) => {
              return [
                <span>
                  {this.numberFormatter(row.destnumber)}
                </span>
              ];
            }
          }
        },
        {
          field: "description",
          title: "Description",
          sortable: true,
          minWidth: 200,
          showOverflow: true
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ];
            }
          }
        }
      ]
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    number: debounce(function() {
      const checkNumberService = feathersClient.service("check-number");
      checkNumberService
        .find({
          query: { tenantId: this.selectedTenant.id, number: this.number }
        })
        .then(res => {
          this.numInUse = res;
        });
    }, 500),

    selectedTenant() {
      this.findShortNumbers();
      feathersClient
        .service("channel-memberships")
        .remove(null, { query: { channelName: this.eventChannel } });
      this.joinSocketChannel();
    },

    user() {
      this.findShortNumbers();
      if (!this.eventChannel) {
        this.joinSocketChannel();
      }
    },

    search: debounce(function() {
      this.searchNumbers();
    }, 500)
  },

  methods: {
    ...mapActions("short-numbers", {
      createShortNumbers: "create",
      patchShortNumbers: "patch",
      deleteShortNumbers: "remove"
    }),

    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map(item => ({
          number: item.number,
          destNumber: item.destnumber,
          description: item.description
        }));

        const options = {
          filename: "short_numbers",
          showLabels: true,
          headers: ["Name", "Destination", "Description"]
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    searchNumbers() {
      if (this.search.length) {
        this.tableData = this.allNumbers.filter(
          data =>
            !this.search ||
            data.number.toLowerCase().includes(this.search.toLowerCase()) ||
            data.destnumber.toLowerCase().includes(this.search.toLowerCase()) ||
            data.description.toLowerCase().includes(this.search.toLowerCase())
        );
      } else {
        this.tableData = [...this.allNumbers];
      }
      this.total = this.tableData.length;
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
      this.numInUse = null;
    },

    openEditModal(id) {
      this.clearFormFields = false;
      this.numInUse = null;
      feathersClient
        .service("short-numbers")
        .get(id)
        .then(res => (this.selectedShortNumber = res));
      this.editVisible = true;
    },

    findShortNumbers() {
      this.tableLoading = true;
      this.tableData = [];
      feathersClient
        .service("short-numbers")
        .find({
          query: {
            tenantId: this.selectedTenant.id
          }
        })
        .then(res => {
          this.tableData = [...res];
          this.allNumbers = [...res];
          this.total = res.length;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },

    checkNumber(number) {
      this.number = number;
    },

    editOperation(params) {
      this.$confirm("Edit short number?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.editProcessing = true;

          const editBody = {
            tenantId: this.selectedTenant.id,
            ...params
          };

          editBody.destnumber = editBody.destnumber.replace("(", "");
          editBody.destnumber = editBody.destnumber.replace(")", "");
          editBody.destnumber = editBody.destnumber.replace(/-/g, "");

          this.patchShortNumbers([this.selectedShortNumber.id, editBody])
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Edited successfully!",
                type: "success"
              });
              this.clearFormFields = true;
              this.editProcessing = false;
              this.cancelOperation("edit");
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
              this.editProcessing = false;
            });
        })
        .catch(() => true);
    },

    deleteOperation(id) {
      this.$confirm("Delete short number?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.tableLoading = true;
          this.deleteShortNumbers(id)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success"
              });
              this.tableLoading = false;
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
              this.tableLoading = false;
            });
        })
        .catch(() => true);
    },

    closeModal() {
      this.createVisible = false;
      this.numInUse = null;
    },

    cancelOperation(type) {
      if (type === "create") {
        this.createVisible = false;
      } else if (type === "edit") {
        this.editVisible = false;
      }
      this.clearFormFields = true;
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    joinSocketChannel() {
      const channelService = feathersClient.service("channel-memberships");
      const data = {
        roomType: "admins",
        tenantId: this.selectedTenant.id
      };
      channelService.create(data).then(res => {
        this.eventChannel = `${data.roomType}/${data.tenantId}`;
      });
    },

    removeListeners() {
      feathersClient.service("short-numbers").removeListener("patched");
      feathersClient.service("short-numbers").removeListener("created");
      feathersClient.service("short-numbers").removeListener("removed");
      globalEventBus.$off("appReconnected");
    }
  },

  created() {
    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    //record was edited
    //always refresh did destination options
    feathersClient.service("short-numbers").on("patched", data => {
      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allNumbers.findIndex(
        item => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allNumbers.splice(updateAllIndex, 1, data);
      }
    });

    //record was created
    feathersClient.service("short-numbers").on("created", data => {
      this.tableData.push(data);
      this.allNumbers.push(data);
    });

    //record was deleted
    feathersClient.service("short-numbers").on("removed", data => {
      const indexToRemove = this.tableData.findIndex(
        item => item.id === data.id
      );
      const indexAllToRemove = this.allNumbers.findIndex(
        item => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allNumbers.splice(indexAllToRemove, 1);
      }
    });
  },

  mounted() {
    if (this.user) {
      this.findShortNumbers();
      this.joinSocketChannel();
    }
  },

  beforeDestroy() {
    this.removeListeners();
    feathersClient
      .service("channel-memberships")
      .remove(null, { query: { channelName: this.eventChannel } });
  },

  filters: {
    formatNumber(value) {
      return `(${value.substr(0, 3)}) ${value.substr(3, 3)}-${value.substr(
        6,
        4
      )}`;
    }
  }
};
</script>
